import React, { useRef, useState } from 'react'
import {
  Button,
  Modal,
  Progress,
  message,
} from '@surecompanies/core_components'
import { FileExcelOutlined } from '@ant-design/icons'
import { getPaymentAssignment } from '../../../api/finance-api-client'
import type { PaymentAssignmentItemDto } from '../../../api/dto/get-payment-assignment.dto'
import {
  buildCSV,
  buildExportParams,
  EXPORT_PAGE_SIZE,
  triggerCSVDownload,
} from './export-csv-payment-assignment'
import type { PaymentAssignmentSortBy, PaymentFilters } from './types'

interface SortState {
  sortBy: PaymentAssignmentSortBy
  sortOrder: 'asc' | 'desc'
}

interface ExportPaymentAssignmentButtonProps {
  filters: PaymentFilters
  sort?: SortState | null
  disabled?: boolean
}

export function ExportPaymentAssignmentButton({
  filters,
  sort,
  disabled = false,
}: ExportPaymentAssignmentButtonProps) {
  const [csvExporting, setCsvExporting] = useState(false)
  const [csvProgress, setCsvProgress] = useState(0)
  const exportCancelRef = useRef(false)

  const handleExportCSV = async () => {
    exportCancelRef.current = false
    setCsvProgress(0)
    setCsvExporting(true)

    try {
      const baseParams = buildExportParams(filters, sort)
      const firstPage = await getPaymentAssignment({
        ...baseParams,
        limit: EXPORT_PAGE_SIZE,
        page: 1,
      })

      if (exportCancelRef.current) return

      const total = firstPage.total
      const totalPages = Math.max(1, Math.ceil(total / EXPORT_PAGE_SIZE))
      setCsvProgress(Math.round((1 / totalPages) * 100))

      const allItems: PaymentAssignmentItemDto[] = [...firstPage.data]

      for (let page = 2; page <= totalPages; page++) {
        if (exportCancelRef.current) return
        const pageData = await getPaymentAssignment({
          ...baseParams,
          limit: EXPORT_PAGE_SIZE,
          page,
        })
        allItems.push(...pageData.data)
        setCsvProgress(Math.round((page / totalPages) * 100))
      }

      if (exportCancelRef.current) return

      const csv = buildCSV(allItems)
      const period = filters.paymentPeriod ?? 'export'
      triggerCSVDownload(csv, `payment-assignments-${period}.csv`)
    } catch {
      if (!exportCancelRef.current) {
        void message.error('Failed to export CSV. Please try again.')
      }
    } finally {
      if (!exportCancelRef.current) {
        setCsvExporting(false)
        setCsvProgress(0)
      }
    }
  }

  const handleCancelExport = () => {
    exportCancelRef.current = true
    setCsvExporting(false)
    setCsvProgress(0)
  }

  return (
    <>
      <Button
        icon={<FileExcelOutlined />}
        onClick={() => void handleExportCSV()}
        type="default"
        size="large"
        disabled={disabled || csvExporting}
      >
        Export CSV
      </Button>

      <Modal
        open={csvExporting}
        title="Exporting CSV"
        footer={<Button onClick={handleCancelExport}>Cancel</Button>}
        closable={false}
        centered
        width={360}
      >
        <div style={{ padding: '16px 0' }}>
          <Progress percent={csvProgress} status="active" />
          <div
            style={{
              marginTop: 8,
              color: '#677786',
              fontSize: 13,
              textAlign: 'center',
            }}
          >
            {csvProgress < 100
              ? `Fetching records… ${csvProgress}%`
              : 'Preparing file…'}
          </div>
        </div>
      </Modal>
    </>
  )
}
